// ComplianceList.jsx - With Delete Option
import { Card, Image, Button, Flex, Typography, Spin, message, Upload, Tag, Space, Popconfirm, Switch } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { complianceType } from "../../constant";
import { 
  CheckOutlined, 
  FilePdfOutlined, 
  FileImageOutlined, 
  UploadOutlined, 
  EyeOutlined,
  FileOutlined,
  ReloadOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import ReactApexChart from "react-apexcharts";
import { useEffect, useState } from "react";
import "./style.css";
import { useFetchSelfRestaurantDataQuery, useUpdateRestaurantSettingMutation } from "../restaurant/restaruant.service";
import { useGetAllDocumentationQuery, useUpsertDocumentationMutation, useDeleteDocumentationMutation } from "./compliance.service";
import { BACKEND_URL } from "../../constant";

const { Title, Text } = Typography;

const piChartOptions = {
  chart: {
    width: 380,
    type: "pie",
  },
  colors: ["#4CAF50", "#F44336"],
  labels: ["Completed", "Not Completed"],
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 200,
        },
        legend: {
          position: "bottom",
        },
      },
    },
  ],
};

export default function ComplianceList() {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const navigate = useNavigate();

  const [state, setState] = useState([0, 100]);
  const [uploading, setUploading] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [deleting, setDeleting] = useState(null);

  const handleNavigation = (type) => {
    navigate(`/compliance_checks/checklist/${type}`);
  };

  const {
    data: restaurantData = {
      data: { restaurant: { complianceLevel: [], manualComplianceSections: [] } },
    },
    isSuccess,
    isLoading,
    refetch: refetchRestaurant,
  } = useFetchSelfRestaurantDataQuery("", { refetchOnMountOrArgChange: true });

  const {
    data: documentationData,
    isFetching: isDocFetching,
    refetch: refetchDocs,
  } = useGetAllDocumentationQuery("", { refetchOnMountOrArgChange: true });

  const [upsertDocumentation] = useUpsertDocumentationMutation();
  const [deleteDocumentation] = useDeleteDocumentationMutation();
  const [requestUpdateRestaurantSetting, { isLoading: isUpdatingManual }] =
    useUpdateRestaurantSettingMutation();

  const {
    data: {
      restaurant: { complianceLevel = [], manualComplianceSections = [] },
    },
  } = restaurantData;

  const documentationList = documentationData?.data?.documentation || [];

  useEffect(() => {
    if (isSuccess) {
      const totalTypes = complianceType.length;
      const completedCount = complianceType.filter(
        (item) =>
          complianceLevel.includes(item.value) ||
          manualComplianceSections.includes(item.value),
      ).length;
      const tmp = Number(((completedCount / totalTypes) * 100).toFixed(1));
      setState([tmp, 100 - tmp]);
    }
  }, [isLoading, complianceLevel, manualComplianceSections, isSuccess]);

  const handleManualComplianceToggle = async (sectionType, checked) => {
    try {
      const nextManual = checked
        ? [...new Set([...manualComplianceSections, sectionType])]
        : manualComplianceSections.filter((type) => type !== sectionType);

      await requestUpdateRestaurantSetting({
        manualComplianceSections: nextManual,
      }).unwrap();

      message.success(
        checked
          ? t("{section} has been marked as completed on paper. It now counts toward your compliance progress.", {
              section: t(sectionType) || sectionType,
            })
          : t("{section} is no longer marked as completed on paper.", {
              section: t(sectionType) || sectionType,
            }),
      );
      refetchRestaurant();
    } catch (error) {
      message.error(
        error?.data?.message ||
          t("Could not update paper completion for {section}. Please check your connection and try again.", {
            section: t(sectionType) || sectionType,
          }),
      );
    }
  };

  const isDocumentUploaded = (type) => {
    return documentationList.some(doc => doc.type === type && doc.isUploaded);
  };

  const getDocumentInfo = (type) => {
    return documentationList.find(doc => doc.type === type);
  };

  const handleFileUpload = async (file, type, title) => {
    setUploading(type);
    setUploadProgress(prev => ({ ...prev, [type]: 0 }));
    
    try {
      const formData = new FormData();
      formData.append("type", type);
      formData.append("file", file);
      
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const newFileName = `${type}_${timestamp}_${baseName}.${fileExtension}`;
      formData.append("fileName", newFileName);

      setUploadProgress(prev => ({ ...prev, [type]: 50 }));
      
      await upsertDocumentation(formData).unwrap();
      
      setUploadProgress(prev => ({ ...prev, [type]: 100 }));
      message.success({
        content: `${title} uploaded successfully!`,
        duration: 3,
        style: { marginTop: 20 },
      });
      refetchDocs();
    } catch (error) {
      console.error("Upload error:", error);
      message.error({
        content: error?.data?.message || "Failed to upload document. Please try again.",
        duration: 4,
        style: { marginTop: 20 },
      });
    } finally {
      setUploading(null);
      setUploadProgress(prev => ({ ...prev, [type]: 0 }));
    }
    return false;
  };

  const handleDeleteDocument = async (type, title) => {
    setDeleting(type);
    try {
      await deleteDocumentation(type).unwrap();
      message.success({
        content: `${title} deleted successfully!`,
        duration: 3,
        style: { marginTop: 20 },
      });
      refetchDocs();
    } catch (error) {
      console.error("Delete error:", error);
      message.error({
        content: error?.data?.message || "Failed to delete document. Please try again.",
        duration: 4,
        style: { marginTop: 20 },
      });
    } finally {
      setDeleting(null);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') {
      return <FilePdfOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />;
    }
    if (fileType === 'image') {
      return <FileImageOutlined style={{ fontSize: 24, color: '#1890ff' }} />;
    }
    return <FileOutlined style={{ fontSize: 24, color: '#999' }} />;
  };

  const getFileTypeLabel = (fileType) => {
    if (fileType === 'pdf') return 'PDF';
    if (fileType === 'image') return 'Image';
    return 'File';
  };

  const documentationTypes = [
    {
      title: "Pest Control Certificate",
      value: "PEST_CONTROL_CERTIFICATE",
      icon: <FilePdfOutlined style={{ fontSize: 28, color: "#ff4d4f" }} />,
      description: "Upload your pest control certificate",
      acceptedFormats: "PDF, JPG, PNG, GIF, WebP",
    },
    {
      title: "Oil Removal Certificate",
      value: "OIL_CERTIFICATE_REMOVAL",
      icon: <FileImageOutlined style={{ fontSize: 28, color: "#1890ff" }} />,
      description: "Upload your oil removal certificate",
      acceptedFormats: "PDF, JPG, PNG, GIF, WebP",
    },
  ];

  return (
    <Card
      title={t("Compliance Checklist")}
      className="compliance-list-page"
      bordered={false}
    >
      {/* Compliance Sections Grid */}
      {complianceType.map((item, key) => {
        const isCompleted =
          complianceLevel.includes(item.value) ||
          manualComplianceSections.includes(item.value);
        const isManual = manualComplianceSections.includes(item.value);

        return (
          <div
            key={key}
            className={`compliance-list-page-item${
              isCompleted ? " compliance-list-page-item--complete" : ""
            }${isManual ? " compliance-list-page-item--manual" : ""}`}
            onClick={() => handleNavigation(item.value)}
          >
            <img
              crossOrigin="anonymous"
              width={200}
              src={item.image_path}
              alt="Current compliance image"
              style={{
                objectFit: "cover",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
              }}
            />
            <div className="compliance-list-page-item-title">
              <h3>{t(item.title)}</h3>
              {isManual && (
                <Tag color="success" className="compliance-manual-badge">
                  {t("Manually completed")}
                </Tag>
              )}
              <CheckOutlined
                className={
                  isCompleted ? "checklist-complete" : "checklist-pending"
                }
              />
            </div>
            <div
              className="compliance-manual-toggle"
              onClick={(event) => event.stopPropagation()}
            >
              <Switch
                size="small"
                checked={isManual}
                loading={isUpdatingManual}
                onChange={(checked) =>
                  handleManualComplianceToggle(item.value, checked)
                }
              />
              <span>{t("Completed on paper")}</span>
            </div>
          </div>
        );
      })}

      {/* Pie Chart */}
      <ReactApexChart
        className="compliance-pie-chart"
        options={piChartOptions}
        series={state}
        type="pie"
        width={380}
      />

      {/* Documentation Section */}
      <div style={{ width: "100%", marginTop: 24 }}>
        <div
          style={{
            borderTop: "1px solid #e8e8e8",
            paddingTop: 24,
            marginBottom: 16,
          }}
        >
          <Flex align="center" justify="space-between" wrap>
            <div>
              <Title level={4} style={{ marginBottom: 4, color: "#2F2E70" }}>
                📄 Documentation
              </Title>
              <Text type="secondary" style={{ fontSize: 14 }}>
                Upload your certificates and documents.
              </Text>
            </div>
            <Tag color="blue" style={{ fontSize: 12 }}>
              {documentationList.filter(d => d.isUploaded).length}/{documentationTypes.length} Uploaded
            </Tag>
          </Flex>
        </div>

        <Spin spinning={isDocFetching}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: 16,
            }}
          >
            {documentationTypes.map((item) => {
              const isUploaded = isDocumentUploaded(item.value);
              const docInfo = getDocumentInfo(item.value);
              const isUploading = uploading === item.value;
              const isDeleting = deleting === item.value;

              return (
                <div
                  key={item.value}
                  style={{
                    padding: "20px",
                    backgroundColor: isUploaded ? "#f6ffed" : "#fafafa",
                    borderRadius: "12px",
                    border: isUploaded 
                      ? "2px solid #b7eb8f" 
                      : "1px dashed #d9d9d9",
                    transition: "all 0.3s ease",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {/* Status Badge */}
                  <div style={{ position: "absolute", top: 12, right: 12 }}>
                    {isUploaded ? (
                      <Tag color="success" style={{ fontSize: 11 }}>✓ Uploaded</Tag>
                    ) : (
                      <Tag color="warning" style={{ fontSize: 11 }}>Pending</Tag>
                    )}
                  </div>

                  {/* Icon */}
                  <div style={{ marginBottom: 12 }}>
                    {isUploaded && docInfo ? (
                      getFileIcon(docInfo.fileType)
                    ) : (
                      item.icon
                    )}
                  </div>

                  {/* Title */}
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      marginBottom: 4,
                      color: "#333",
                    }}
                  >
                    {item.title}
                  </div>

                  {/* Description / File Info */}
                  <div
                    style={{
                      fontSize: 13,
                      color: "#999",
                      marginBottom: 12,
                      minHeight: "40px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {isUploaded && docInfo ? (
                      <Space direction="vertical" size={2}>
                        <Text ellipsis style={{ maxWidth: "200px", fontSize: 13 }}>
                          📎 {docInfo.fileName || 'Document'}
                        </Text>
                        <Tag color="blue" style={{ fontSize: 10 }}>
                          {getFileTypeLabel(docInfo.fileType)}
                        </Tag>
                      </Space>
                    ) : (
                      <span>{item.description}</span>
                    )}
                  </div>

                  {/* Supported formats */}
                  {!isUploaded && (
                    <div style={{ fontSize: 11, color: "#bbb", marginBottom: 12 }}>
                      Supports: {item.acceptedFormats} • Max 10MB
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div style={{ display: "flex", gap: 8, marginTop: "auto", flexWrap: "wrap", justifyContent: "center" }}>
                    <Upload
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        const isValidType = file.type === 'application/pdf' || 
                                           file.type.startsWith('image/');
                        if (!isValidType) {
                          message.error('Please upload PDF or image file');
                          return false;
                        }
                        const isLt10M = file.size / 1024 / 1024 < 10;
                        if (!isLt10M) {
                          message.error('File must be smaller than 10MB');
                          return false;
                        }
                        handleFileUpload(file, item.value, item.title);
                        return false;
                      }}
                    >
                      <Button
                        type="primary"
                        icon={isUploaded ? <ReloadOutlined /> : <UploadOutlined />}
                        loading={isUploading}
                        style={{
                          backgroundColor: isUploaded ? "#52c41a" : "#2F2E70",
                          borderColor: isUploaded ? "#52c41a" : "#2F2E70",
                          minWidth: "80px",
                        }}
                      >
                        {isUploaded ? "Replace" : "Upload"}
                      </Button>
                    </Upload>

                    {isUploaded && docInfo && (
                      <>
                        <Button
                          type="default"
                          icon={<EyeOutlined />}
                          onClick={() => {
                            if (docInfo?.fileUrl) {
                              window.open(`${BACKEND_URL}${docInfo.fileUrl}`, "_blank");
                            }
                          }}
                        >
                          View
                        </Button>

                        {/* Delete Button with Popconfirm */}
                        <Popconfirm
                          title="Delete Document"
                          description={`Are you sure you want to delete "${item.title}"?`}
                          onConfirm={() => handleDeleteDocument(item.value, item.title)}
                          okText="Yes, Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            loading={isDeleting}
                            style={{ minWidth: "60px" }}
                          >
                            Delete
                          </Button>
                        </Popconfirm>
                      </>
                    )}
                  </div>

                  {/* Upload Progress */}
                  {isUploading && uploadProgress[item.value] > 0 && (
                    <div style={{ width: "100%", marginTop: 12 }}>
                      <div style={{
                        height: 4,
                        background: "#f0f0f0",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}>
                        <div style={{
                          width: `${uploadProgress[item.value]}%`,
                          height: "100%",
                          background: "linear-gradient(90deg, #52c41a, #1890ff)",
                          borderRadius: 2,
                          transition: "width 0.3s",
                        }} />
                      </div>
                      <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                        Uploading... {uploadProgress[item.value]}%
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Spin>
      </div>
    </Card>
  );
}